// imagepaste リサイズ (A案 = 送信前リサンプル, 2026-10-10).
//
// 貼った本人のブラウザで元 bitmap を目標サイズへ描き直し、その結果ピクセルを
//   (1) プレビュー overlay の rgba (= 本人の画面)
//   (2) アップロードする PNG Blob (= 確定後に全員へ 1:1 で blit される画像)
// の両方に使う。両者は **同一関数 `resampleBitmapToRgba` の出力**から作るので、
// 「プレビューで見たピクセル = 確定後に描かれるピクセル」(WYSIWYG) が構造的に保証される。
// 受信側 / server / snapshot は natural size の rgba を 1:1 で blit するだけで無変更。
//
// 元 bitmap から毎回再サンプルする (= 縮小→拡大の劣化累積を避ける)。

import { createRaster2d, raster2dToBlob } from '../canvas2d/rasterCanvas.js';

/** `(bitmap, width, height)` → リサンプル済み RGBA。テストで差し替え可能 (DI)。 */
export type ImagepasteResampleFn = (
  bitmap: ImageBitmap,
  width: number,
  height: number,
) => { rgba: Uint8ClampedArray; width: number; height: number };

/** RGBA → PNG Blob (null = エンコード不可)。テストで差し替え可能 (DI)。 */
export type ImagepastePngBlobFn = (
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
) => Promise<Blob | null>;

/** 目標サイズの下限 (px)。 */
export const IMAGEPASTE_MIN_SIZE = 1;

/** 目標サイズを整数 >= 1 に正規化する。非有限値は 1。 */
export const normalizeImagepasteSize = (n: number): number =>
  Number.isFinite(n) ? Math.max(IMAGEPASTE_MIN_SIZE, Math.round(n)) : IMAGEPASTE_MIN_SIZE;

/**
 * 元 bitmap を `width` x `height` (整数 >= 1) へ高品質補間で描き直した RGBA を返す。
 * 2D コンテキストが得られない環境では throw (設定ミス、rasterCanvas と同方針)。
 */
export const resampleBitmapToRgba: ImagepasteResampleFn = (bitmap, width, height) => {
  const w = normalizeImagepasteSize(width);
  const h = normalizeImagepasteSize(height);
  const raster = createRaster2d(w, h);
  if (raster === null) {
    throw new Error('no 2D canvas available (neither OffscreenCanvas nor document)');
  }
  const ctx = raster.ctx;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height, 0, 0, w, h);
  const img = ctx.getImageData(0, 0, w, h);
  // canvas backing memory を手放せるよう、呼び出し側所有のコピーを返す。
  return { rgba: new Uint8ClampedArray(img.data), width: w, height: h };
};

/** RGBA を PNG Blob にエンコードする既定実装。 */
export const encodeRgbaToPngBlob: ImagepastePngBlobFn = async (rgba, width, height) => {
  const raster = createRaster2d(width, height);
  if (raster === null) return null;
  const data = rgba as Uint8ClampedArray<ArrayBuffer>;
  raster.ctx.putImageData(new ImageData(data, width, height), 0, 0);
  return await raster2dToBlob(raster, 'image/png');
};
